"use client";

import { useMemo, useState } from "react";
import { Volume2 } from "lucide-react";
import { Badge } from "@modernatc/ui/badge";
import { Button } from "@modernatc/ui/button";
import { Input } from "@modernatc/ui/input";
import { Label } from "@modernatc/ui/label";
import type { Scenario, Slot } from "../_data/scenarios";

/**
 * Phraseology Studio — interactive .65-cited phraseology generator.
 *
 * Why this exists: 123atc and similar free sims play game audio with
 * loose, inconsistent wording. Real ATC instructors hammer phraseology
 * for weeks because the .65 is precise about it. This tool lets a
 * student pick a scenario, fill in the variables, and see EXACTLY the
 * canonical wording — with every variable highlighted so they can see
 * the pattern, plus a one-click read-aloud (browser speech synthesis)
 * so they can hear it spoken.
 *
 * Architecture: scenarios + slot definitions live in _data/scenarios.ts
 * as a flat array. This component renders the picker, the input form,
 * and the rendered phraseology preview. No external API, no signup.
 */

interface StudioProps {
  scenarios: Scenario[];
  /** Initially selected scenario id; falls back to scenarios[0]. */
  initialId?: string;
}

export function PhraseologyStudio({ scenarios, initialId }: StudioProps) {
  const [activeId, setActiveId] = useState<string>(
    initialId && scenarios.some((s) => s.id === initialId)
      ? initialId
      : scenarios[0]?.id ?? "",
  );

  const active = useMemo(
    () => scenarios.find((s) => s.id === activeId) ?? scenarios[0],
    [scenarios, activeId],
  );

  // Each scenario keeps its own slot values. Switching between scenarios
  // resets to defaults so we don't carry over a "callsign" from a
  // takeoff clearance into a holding-instructions form half-filled.
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});

  if (!active) return null;

  const current = values[active.id] ?? Object.fromEntries(active.slots.map((s) => [s.key, s.default]));

  const setSlot = (key: string, value: string) => {
    setValues((prev) => ({
      ...prev,
      [active.id]: { ...current, [key]: value },
    }));
  };

  const resetSlots = () => {
    setValues((prev) => ({
      ...prev,
      [active.id]: Object.fromEntries(active.slots.map((s) => [s.key, s.default])),
    }));
  };

  const rendered = active.template
    .map((seg) => (seg.kind === "text" ? seg.text : current[seg.slot]))
    .join(" ")
    .replace(/\s+([,.])/g, "$1") // tidy spaces before punctuation
    .replace(/\s+/g, " ")
    .trim();

  const speak = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(rendered);
    utterance.rate = 1.05;
    utterance.pitch = 1;
    // Try to pick an English voice — most browsers ship one by default.
    const voice = window.speechSynthesis.getVoices().find((v) => v.lang.startsWith("en"));
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      {/* Scenario picker — left rail on desktop, top dropdown on mobile. */}
      <aside className="rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] p-3">
        <div className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
          Scenarios
        </div>
        <ul className="mt-2 flex max-h-[60vh] flex-col gap-0.5 overflow-y-auto pr-1">
          {scenarios.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => setActiveId(s.id)}
                className={`w-full rounded-[var(--radius-sm)] px-3 py-2 text-left text-sm transition ${
                  s.id === active.id
                    ? "bg-[var(--color-panel-raised)] text-[var(--color-ink)]"
                    : "text-[var(--color-ink-dim)] hover:bg-[var(--color-panel-raised)]/40 hover:text-[var(--color-ink)]"
                }`}
              >
                <div className="text-[13px] font-medium leading-tight">
                  {s.title}
                </div>
                <div className="text-callsign mt-1 text-[10px] text-[var(--color-ink-muted)]">
                  {s.category} · {s.citation.doc} {s.citation.paragraph}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main panel. */}
      <section className="rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Badge variant="muted">{active.category}</Badge>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[var(--color-ink)]">
              {active.title}
            </h2>
            <p className="mt-2 text-sm text-[var(--color-ink-dim)]">
              {active.summary}
            </p>
            <p className="mt-2 text-callsign text-[10px] uppercase tracking-wide text-[var(--color-amber)]">
              {active.citation.doc} § {active.citation.paragraph} — {active.citation.title}
            </p>
          </div>
        </div>

        {/* Rendered phraseology with highlighted slots. */}
        <div className="mt-6 rounded-[var(--radius-md)] border border-[var(--color-amber)]/30 bg-[rgb(212_158_82_/_0.06)] p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
              {active.speaker} transmits
            </div>
            <Button variant="ghost" size="sm" onClick={speak} title="Speak out loud">
              <Volume2 className="size-3.5" />
              Speak
            </Button>
          </div>
          <p className="mt-3 text-balance text-lg leading-relaxed text-[var(--color-ink)] sm:text-xl">
            <HighlightedPhraseology scenario={active} values={current} />
          </p>
        </div>

        {/* Slot inputs. */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--color-ink)]">
              Edit the variables
            </h3>
            <button
              type="button"
              onClick={resetSlots}
              className="text-xs text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
            >
              Reset to defaults
            </button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {active.slots.map((slot) => (
              <SlotInput
                key={slot.key}
                slot={slot}
                value={current[slot.key] ?? ""}
                onChange={(v) => setSlot(slot.key, v)}
              />
            ))}
          </div>
        </div>

        {/* Notes / gotchas. */}
        {active.notes && active.notes.length > 0 ? (
          <div className="mt-6 rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] p-5">
            <h3 className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
              Common gotchas
            </h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-[var(--color-ink-dim)]">
              {active.notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function HighlightedPhraseology({
  scenario,
  values,
}: {
  scenario: Scenario;
  values: Record<string, string>;
}) {
  return (
    <>
      {scenario.template.map((seg, i) => {
        if (seg.kind === "text") {
          // Trim leading/trailing spaces so commas/periods sit tight.
          return <span key={i}>{seg.text} </span>;
        }
        const value = values[seg.slot] || "";
        return (
          <span
            key={i}>
            <span className="text-mono rounded-[var(--radius-md)] bg-[var(--color-amber)]/15 px-1.5 py-0.5 text-[var(--color-amber-bright)]"
          >
            {value}
          </span>{" "}
          </span>
        );
      })}
    </>
  );
}

function SlotInput({
  slot,
  value,
  onChange,
}: {
  slot: Slot;
  value: string;
  onChange: (v: string) => void;
}) {
  if (slot.kind === "option" && slot.options) {
    return (
      <div>
        <Label htmlFor={`slot-${slot.key}`} className="text-xs">
          {slot.label}
        </Label>
        <select
          id={`slot-${slot.key}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 flex h-9 w-full rounded-[var(--radius-sm)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] px-3 text-sm text-[var(--color-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--color-amber)]/40"
        >
          {slot.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {slot.hint ? (
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{slot.hint}</p>
        ) : null}
      </div>
    );
  }
  return (
    <div>
      <Label htmlFor={`slot-${slot.key}`} className="text-xs">
        {slot.label}
      </Label>
      <Input
        id={`slot-${slot.key}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-mono mt-1 text-sm"
      />
      {slot.hint ? (
        <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{slot.hint}</p>
      ) : null}
    </div>
  );
}
